import React, { useState, useEffect, useRef } from 'react';
import { Mic, Sparkles, Building2, Share2, MessageCircle, Phone, Globe, Mail, Clock, Play, X, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import FullscreenForm from '../components/FullscreenForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Series Card Component
const SeriesCard = ({ series, onClick }) => {
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef(null);
  
  // Get first episode for preview
  const previewEpisode = series.episodes?.[0];

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current && previewEpisode?.streaming_url) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div 
      className="group relative aspect-[9/16] sm:aspect-[3/4] bg-white/5 border border-white/10 overflow-hidden cursor-pointer"
      onClick={() => onClick(series)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video/Image Preview */}
      {previewEpisode?.streaming_url ? (
        <video
          ref={videoRef}
          src={previewEpisode.streaming_url}
          className="w-full h-full object-cover"
          muted
          playsInline
          loop
          preload="metadata"
          poster={series.thumbnail_url || previewEpisode.thumbnail_url || ''}
        />
      ) : series.thumbnail_url ? (
        <img 
          src={series.thumbnail_url} 
          alt={series.title}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-white/5">
          <Mic className="w-12 h-12 text-white/20" />
        </div>
      )}
      
      {/* Play icon overlay */}
      <div className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300 ${isHovering ? 'opacity-0' : 'opacity-100'}`}>
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
          <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white" />
        </div>
      </div>
      
      {/* Episode count badge */}
      <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs">
        {series.episodes?.length || 0} episodes
      </div>
      
      {/* Title & Description */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
        <h3 className="text-white font-medium text-sm sm:text-base mb-1">{series.title}</h3>
        {series.description && (
          <p className="text-white/50 text-xs line-clamp-2 hidden sm:block">{series.description}</p>
        )}
      </div>
    </div>
  );
};

// Playlist Modal Component - Improved with visible controls and episode list
const PlaylistModal = ({ series, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showEpisodeList, setShowEpisodeList] = useState(false);
  const videoRef = useRef(null);

  const episodes = series.episodes || [];
  const currentEpisode = episodes[currentIndex];

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!currentEpisode) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
      {/* Fixed Top Bar - Always Visible */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 bg-black border-b border-white/10">
        <div className="flex items-center gap-4">
          <button 
            onClick={onClose}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <h3 className="text-white font-medium text-sm sm:text-base">{series.title}</h3>
            <p className="text-white/50 text-xs">{currentEpisode.title}</p>
          </div>
        </div>
        <button
          onClick={() => setShowEpisodeList(!showEpisodeList)}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm transition-colors flex items-center gap-2"
        >
          <span className="hidden sm:inline">All Episodes</span>
          <span className="text-white/60">({episodes.length})</span>
        </button>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video Player */}
        <div className={`flex-1 flex items-center justify-center bg-black p-4 transition-all ${showEpisodeList ? 'lg:mr-80' : ''}`}>
          <div className="w-full max-w-4xl">
            <div className="relative aspect-video bg-black rounded overflow-hidden">
              <video
                ref={videoRef}
                key={currentEpisode.id}
                src={currentEpisode.streaming_url || currentEpisode.video_url}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                poster={currentEpisode.thumbnail_url || ''}
              />
            </div>
            
            {/* Episode Info Below Video */}
            <div className="mt-4 p-4 bg-white/5 rounded">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/40 text-xs mb-1">Episode {currentIndex + 1} of {episodes.length}</p>
                  <h4 className="text-white font-medium">{currentEpisode.title}</h4>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentIndex(prev => prev > 0 ? prev - 1 : episodes.length - 1)}
                    disabled={episodes.length <= 1}
                    className="p-2 bg-white/10 hover:bg-white/20 disabled:opacity-30 rounded text-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentIndex(prev => prev < episodes.length - 1 ? prev + 1 : 0)}
                    disabled={episodes.length <= 1}
                    className="p-2 bg-white/10 hover:bg-white/20 disabled:opacity-30 rounded text-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Episode List Sidebar */}
        <div className={`fixed lg:relative right-0 top-0 bottom-0 w-80 bg-[#0a0a0a] border-l border-white/10 overflow-y-auto transition-transform duration-300 ${
          showEpisodeList ? 'translate-x-0' : 'translate-x-full lg:hidden'
        }`} style={{ top: '73px' }}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium">Episodes</h4>
              <button 
                onClick={() => setShowEpisodeList(false)}
                className="lg:hidden p-1 text-white/40 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {episodes.map((episode, idx) => (
                <button
                  key={episode.id}
                  onClick={() => { setCurrentIndex(idx); setShowEpisodeList(false); }}
                  className={`w-full p-3 text-left rounded transition-colors ${
                    idx === currentIndex 
                      ? 'bg-white/20 text-white' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{episode.title}</p>
                      {idx === currentIndex && (
                        <p className="text-xs text-white/50 mt-0.5">Now Playing</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PodcastsPage = () => {
  const [series, setSeries] = useState([]);
  const [activeSeries, setActiveSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/podcast-series`);
      if (res.ok) {
        const data = await res.json();
        setSeries(data);
      }
    } catch (e) {
      console.error('Error fetching series:', e);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      id: 'intro',
      title: "Let's Get Started",
      subtitle: 'First, tell us about yourself',
      icon: Sparkles,
      fields: [
        { name: 'name', label: 'Your Name', placeholder: 'What should we call you?', type: 'text', required: true },
        { name: 'email', label: 'Email Address', placeholder: 'your@email.com', type: 'email', required: true }
      ]
    },
    {
      id: 'business',
      title: 'Your Business',
      subtitle: "Tell us about what you do",
      icon: Building2,
      fields: [
        { name: 'businessName', label: 'Business Name', placeholder: 'Your company or brand name', type: 'text', required: true },
        { name: 'yearsInBusiness', label: 'Years in Business', placeholder: 'How long have you been at it?', type: 'text', required: true }
      ]
    },
    {
      id: 'social',
      title: 'Stay Connected',
      subtitle: 'Where can people find you?',
      icon: Share2,
      fields: [
        { name: 'socialMedia', label: 'Social Media Handles', placeholder: '@instagram, @twitter, etc.', type: 'text', required: false },
        { name: 'country', label: 'Country', placeholder: 'Where are you based?', type: 'text', required: true }
      ]
    },
    {
      id: 'story',
      title: 'Your Journey',
      subtitle: 'This is the exciting part',
      icon: MessageCircle,
      fields: [
        { name: 'yourStory', label: 'Your Story', placeholder: 'Tell us about your journey... How did it all begin? Any funny stories along the way?', type: 'textarea', required: true }
      ]
    },
    {
      id: 'unique',
      title: 'Stand Out',
      subtitle: 'What makes you different?',
      icon: Sparkles,
      fields: [
        { name: 'whatMakesUnique', label: 'What Makes You Unique?', placeholder: 'What sets your business apart? What are you most proud of?', type: 'textarea', required: true }
      ]
    },
    {
      id: 'contact',
      title: 'Almost There',
      subtitle: "How can we reach you?",
      icon: Phone,
      fields: [
        { name: 'contactNumber', label: 'Contact Number', placeholder: '+1 (555) 000-0000', type: 'tel', required: true }
      ]
    }
  ];

  const handleSubmit = async (formData) => {
    const response = await fetch(`${API_URL}/api/podcast-application`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to submit');
    }
    
    return response.json();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Podcasts - Share Your Story"
        description="Join our podcast! We're looking for business owners, entrepreneurs, and anyone with a unique journey to share. Book your session with CreativeDirectorKhan | KHAN."
        keywords="podcast, business stories, entrepreneur interviews, storytelling, worldwide podcast"
        canonical="https://creativedirectorkhan.com/podcasts"
      />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 px-4 py-2 border border-white/10 bg-white/[0.02] mb-8">
            <Mic className="w-4 h-4 text-white/60" />
            <span className="text-white/60 text-xs tracking-[0.2em] uppercase">The Podcast</span>
          </div>
          
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white leading-[0.95] mb-6">
            Have a Story
            <span className="block text-white/40 italic">to Tell?</span>
          </h1>
          
          <p className="text-white/60 text-lg max-w-xl mx-auto leading-relaxed mb-4">
            We&apos;re looking for business owners, entrepreneurs, beauty salons, anyone and everyone with a journey.
          </p>
          
          <p className="text-white/50 max-w-lg mx-auto leading-relaxed mb-12">
            We wanna hear your journey, funny stories, what makes you and your business unique over a fun chat.
          </p>

          {/* Form Button */}
          <div className="max-w-md mx-auto">
            <FullscreenForm
              steps={steps}
              onSubmit={handleSubmit}
              title="Podcast Application"
              submitButtonText="Submit"
              successTitle="Thank You!"
              successMessage="We've received your application and can't wait to hear more about your story. Our team will reach out within 48 hours."
            />
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-white/40 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>~3 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span>Worldwide</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>48hr response</span>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast Series Gallery */}
      {series.length > 0 && (
        <section className="py-20 px-6 md:px-12 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-4">Watch Our Episodes</p>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-white">Podcast Series</h2>
            </div>

            {/* Series Grid - Mobile optimized */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
              {series.map(s => (
                <SeriesCard 
                  key={s.id} 
                  series={s} 
                  onClick={setActiveSeries}
                />
              ))}
            </div>

            {loading && (
              <div className="text-center py-16">
                <p className="text-white/40">Loading series...</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Playlist Modal */}
      {activeSeries && (
        <PlaylistModal 
          series={activeSeries} 
          onClose={() => setActiveSeries(null)} 
        />
      )}
    </div>
  );
};

export default PodcastsPage;
